import { useState } from "react";
import { useApi, apiPost } from "@/lib/api";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { SeverityChip } from "@/components/common/SeverityChip";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fmtCurrency } from "@/lib/format";
import { Sparkles, Lightbulb, ArrowRight, Wand2 } from "lucide-react";

export default function CompanionHome() {
  const { data, isLoading, error } = useApi("companion-home", "/companion/home");
  const [task, setTask] = useState("");
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);

  const suggest = async () => {
    if (!task.trim()) return;
    setBusy(true); setRec(null);
    try { setRec(await apiPost("/ai/advisor", { task })); } catch { setRec({ error: true }); } finally { setBusy(false); }
  };
  if (error) return <ErrorState />;

  return (
    <div>
      <PageHeader eyebrow="Navitra Companion" title="Start any AI task the right way" subtitle="Describe your task and Navitra suggests the approved tool and model — then improve your prompt before you send it." />

      <Card className="mb-4 border-gold/30">
        <CardContent className="pt-6">
          <Textarea value={task} onChange={(e) => setTask(e.target.value)} placeholder="e.g. Summarise this 80-page vendor contract and flag risky clauses…" className="min-h-[80px] text-base" data-testid="home-task-input" />
          <div className="flex justify-between items-center mt-3">
            <p className="text-xs text-muted-foreground">Powered by your active LLM engine · answers are governed by policy</p>
            <Button onClick={suggest} disabled={busy} data-testid="home-suggest"><Sparkles className="h-4 w-4 mr-1.5" />{busy ? "Thinking…" : "Suggest a tool"}</Button>
          </div>
          {busy && <LoadingBlock className="h-24 mt-4" />}
          {rec && !rec.error && (
            <div className="mt-4 rounded-xl border bg-muted/30 p-4" data-testid="home-suggestion">
              <div className="flex items-center gap-2 mb-2"><SeverityChip level="success" label={rec.recommended_tool} dot /><SeverityChip level="info" label={rec.model_tier} /></div>
              <p className="text-sm">{rec.why}</p>
              {rec.data_caution && <p className="text-xs text-sev-warning mt-2">⚠ {rec.data_caution}</p>}
              <Button asChild variant="outline" size="sm" className="mt-3"><Link to="/companion/prompt-studio"><Wand2 className="h-3.5 w-3.5 mr-1.5" />Improve my prompt <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></Link></Button>
            </div>
          )}
          {rec?.error && <p className="mt-4 text-sm text-sev-danger">Advisor unavailable — check the active LLM engine in Super Admin.</p>}
        </CardContent>
      </Card>

      {isLoading || !data ? <LoadingBlock /> : (
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="font-display text-lg">Your approved tools</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-3">
              {data.approved_tools.map((t) => (
                <div key={t.id} className="rounded-xl border p-4 hover:border-gold/50 transition-colors">
                  <p className="font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t.notes}</p>
                </div>
              ))}
            </CardContent>
          </Card>
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-display text-lg">This month</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Your usage</span><span className="font-display text-xl">{fmtCurrency(data.month_usage_usd)}</span></div>
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">You saved</span><span className="font-display text-xl text-sev-success">{fmtCurrency(data.month_savings_usd)}</span></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-display text-lg flex items-center gap-2"><Lightbulb className="h-4 w-4 text-gold" />Smart tips</CardTitle></CardHeader>
              <CardContent className="space-y-2">{data.tips.map((t, i) => <p key={i} className="text-sm border-l-2 border-gold/40 pl-3">{t}</p>)}</CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
